/*** Libraries */
import React, { useEffect, useState } from "react";
import { Container, Row, Col, Form, Button, Table } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import ReactQuill from "react-quill";

/***  Constant, Services  */
import { BannerConstants, BannerData } from './banner.types';
import { useLoader } from '../../context/LoaderContext';
import ToggleSwitch from "../../components/Toggle-Switch/ToggleSwitch";
import ConfirmationModal from "../../components/Confirmation-Modal/Confirmation-Modal";
import Banner from "../../components/Banner/Banner";
import { bannerService } from "./bannerAPI";

// Images
import calendarIcon from "../../assets/images/calendarIcon.svg";

/** Styles */
import './banner.style.css';
import "react-datepicker/dist/react-datepicker.css";
import "react-quill/dist/quill.snow.css"

const BannerForm: React.FC = () => {
    type BannerFormErrors = {
        [K in keyof BannerData]?: string;
    };
    const { setLoading } = useLoader();
    const [form, setForm] = useState<BannerData>({
        id: '',
        message: '',
        startDate: null,
        endDate: null,
        visibility: true
    });

    const [editForm, setEditForm] = useState<BannerData>({
        id: '',
        message: '',
        startDate: null,
        endDate: null,
        visibility: true
    });

    const [errors, setErrors] = useState<BannerFormErrors>({});
    const [isOn, setIsOn] = useState(true);
    const [isEdit, setIsEdit] = useState(false);
    const [isPreviewBanner, setPreviewBanner] = useState(false);
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);
    const [shouldDisplayDeleteConfirmModal, setDeleteConfirmModalVisibility] = useState<boolean>(false);
    const [bannermessages, setBannerMessages] = useState<string[]>([]);
    const [bannerListData, setBannerListData] = useState<BannerData[]>([]);

    const modules = {
        toolbar: [
            [{ header: [1, 2, 3, false] }],
            [{ color: [] }, { background: [] }],
            ["bold", "italic", "underline"],
            ["link"],
            [{ list: "ordered" }, { list: "bullet" }],
            ["clean"],
        ],
    };

    const formats = [
        "bold",
        "italic",
        "underline",
        "color",
        "background",
        "link",
        "header",
        "list",
    ];


    /**
     *  Fetch banner
     */
    const fetchBanner = async () => {
        try {
            setLoading(true);
            let result = await bannerService.fetchBanners();
            setLoading(false);
            if (result && result[0]?.id) {
                setBannerListData(prev => [...prev, ...result]);
            }
        } catch (error: any) {
            console.log(error);
            setLoading(false);
            const errorMessage = error?.response?.data?.message || BannerConstants.ERROR_FETCH_BANNER;
            toast.error(errorMessage);
        }
    };

    useEffect(() => {
        fetchBanner();
    }, []);


    useEffect(() => {
        if (bannerListData.length > 0) {
            setForm(bannerListData[0]);
            setStartDate(bannerListData[0].startDate);
            setEndDate(bannerListData[0].endDate);
            setIsOn(bannerListData[0].visibility);
        }
    }, [bannerListData]);

    /**
     * Validate form fields
     * @param data 
     * @returns 
     */
    const validateBannerFields = (data: BannerData): BannerFormErrors => {
        try {
            const errs: BannerFormErrors = {};
            if (!data.message.trim() || data.message == "<p><br></p>") {
                errs.message = BannerConstants.ERROR_Message_REQUIRED;
            }
            if (!data.startDate) {
                errs.startDate = BannerConstants.ERROR_STARTDATE_REQUIRED;
            }
            if (!data.endDate) {
                errs.endDate = BannerConstants.ERROR_ENDDATE_REQUIRED;
            }
            return errs;
        } catch (exe) {
            console.log(exe);
            return {}
        }
    };

    /**
     * On change
     * @param e 
     */
    const handleReactQuillChange = (message: string) => {
        try {
            setForm({ ...form, ['message']: message });
            setErrors(prev => ({ ...prev, ['message']: undefined }));
        } catch (exe) {
            console.log(exe);
        }
    };

    /**
    * On change
    * @param e 
    */
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        try {
            setForm({ ...form, [e.target.name]: e.target.value });
            setErrors(prev => ({ ...prev, [e.target.name]: undefined }));
        } catch (exe) {
            console.log(exe);
        }
    };

    /**
   * Handle date change
   * @param dates 
   */
    const handleDateChange = (dates: [Date | null, Date | null]) => {
        try {
            const [start, end] = dates;
            setStartDate(start ?? null);
            setEndDate(end ?? null);
            if (start) {
                setErrors(prev => ({ ...prev, ['startDate']: undefined }));
            }
            if (end) {
                setErrors(prev => ({ ...prev, ['endDate']: undefined }));
            }
        } catch (exe) {
            console.log(exe);
        }
    };

    /**
     * Format Date
     * @param date 
     * @returns 
     */
    const formatDate = (date: string | Date | undefined): string => {
        try {
            if (!date) return "";

            const d = typeof date === "string" ? new Date(date) : date;

            if (isNaN(d.getTime())) return "";

            const day = d.getDate().toString().padStart(2, "0");
            const month = (d.getMonth() + 1).toString().padStart(2, "0");
            const year = d.getFullYear();

            return `${month}. ${day}. ${year}`;
        } catch (err) {
            console.error(err);
            return "";
        }
    };

    /**
     * Format date range
     */
    const formattedDateRange =
        startDate && endDate
            ? `(${formatDate(startDate)} - ${formatDate(endDate)})`
            : startDate
                ? `(${formatDate(startDate)} - )`
                : "";

    /**
     * On submit
     * @param e 
     * @returns 
     */
    const handleSubmit = async (e: React.FormEvent) => {
        try {
            e.preventDefault();
            const newBanner: BannerData = { ...form, id: '123' };
            newBanner.startDate = startDate;
            newBanner.endDate = endDate;
            const validationErrors = validateBannerFields(newBanner);

            if (Object.keys(validationErrors).length > 0) {
                setErrors(validationErrors);
                return;
            }

            setLoading(true);
            newBanner.visibility = isOn;
            const result = await bannerService.addBanner(newBanner);
            setLoading(false);
            if (result) {
                toast.success(BannerConstants.BANNER_ADDED);
                //setForm({id:'', message: '', startDate: null, endDate: null, visibility: true });
                //setStartDate(null);
                //setEndDate(null);
            } else {
                toast.error(BannerConstants.ERROR_ADD_BANNER);
            }
        } catch (error: any) {
            setLoading(false);
            let errorMessage = BannerConstants.ERROR_ADD_BANNER;
            if (typeof error === "string") {
                errorMessage = error
            }
            toast.error(errorMessage);
        }
    };

    /**
     * On update
     * @param e 
     * @returns 
     */
    const updateBanner = async () => {
        try {
            let newBanner: BannerData = { ...form };
            newBanner.startDate = startDate;
            newBanner.endDate = endDate;
            const validationErrors = validateBannerFields(newBanner);

            if (Object.keys(validationErrors).length > 0) {
                setErrors(validationErrors);
                return;
            }

            setLoading(true);
            newBanner.visibility = isOn;
            const result = await bannerService.updateBanner(newBanner);
            setLoading(false);
            if (result) {
                toast.success(BannerConstants.BANNER_UPDATED);
                setIsEdit(false);
            } else {
                toast.error(BannerConstants.ERROR_UPDATE_BANNER);
            }
        } catch (error: any) {
            setLoading(false);
            let errorMessage = BannerConstants.ERROR_UPDATE_BANNER;
            if (typeof error === "string") {
                errorMessage = error
            }
            toast.error(errorMessage);
        }
    };

    /**
    * On Edit
    */
    const editBanner = () => {
        setIsEdit(true);
        setEditForm(form);
    };


    /**
    * On Cancel edit
    */
    const cancelEditBanner = () => {
        setForm(editForm);
        setStartDate(editForm.startDate);
        setEndDate(editForm.endDate);
        setIsOn(editForm.visibility);
        setIsEdit(false);
    };


    /**
    * Handle toogle
    */
    const handleToggle = () => {
        const newValue = !isOn;
        setIsOn(newValue);
    };

    /**
     * After confirm delete action
     */
    const handleDelete = async () => {
        try {
            setDeleteConfirmModalVisibility(false);

            let newBanner: BannerData = { ...form };
            let tempArray: Array<BannerData> = [];
            tempArray.push(newBanner);

            setLoading(true);
            const result = true; //await dispatch(createBanner(newBanner)).unwrap();
            setLoading(false);
            if (result) {
                toast.success(BannerConstants.BANNER_UPDATED);
                setForm({ id: '', message: '', startDate: null, endDate: null, visibility: true });
                setStartDate(null);
                setEndDate(null);
                setIsOn(true);
            } else {
                toast.error(BannerConstants.ERROR_DATE_REQUIRED);
            }
        } catch (error: any) {
            setLoading(false);
            setDeleteConfirmModalVisibility(false);
            let errorMessage = BannerConstants.ERROR_DELETE_BANNER;
            if (typeof error === "string") {
                errorMessage = error
            }
            toast.error(errorMessage);
        }
    };

    /**
    * Close delete confirm modal
    */
    const handleCloseModal = () => {
        try {
            setDeleteConfirmModalVisibility(false);
        } catch (exe) {
            console.log(exe);
        }
    };

    /**
     * Confirm delete action
     * @param userData 
     */
    const confirmDelete = () => {
        try {
            setDeleteConfirmModalVisibility(true);
        } catch (exe) {
            console.log(exe);
        }
    };

    /**
     * On banner close
     */
    const onCloseBanner = () => {
        setPreviewBanner(false);
    }

    /**
     * On Preview banner
     */
    const previewBanner = () => {
        setBannerMessages([form.message])
        setPreviewBanner(true);
    }

    return (
        <>
            {
                (isPreviewBanner && bannermessages.length > 0) &&
                <Banner
                    messages={bannermessages}
                    autoHideTime={0}
                    onClose={() => onCloseBanner()}
                />
            }
            <div className="banner-from bg-white p-4 rounded shadow-sm mb-4">
                <Form onSubmit={handleSubmit}>
                    <Row className="mb-3">
                        <Col md={12}>
                            {/* <Form.Control
                                disabled={form.id != '' && form.id != null && !isEdit}
                                name="message"
                                as="textarea"
                                rows={5}
                                placeholder="Banner Message"
                                value={form.message}
                                onChange={handleChange}
                                isInvalid={!!errors.message}
                            />
                            <Form.Control.Feedback type="invalid">
                                {errors.message}
                            </Form.Control.Feedback> */ }
                            <div className={`${errors.message ? "is-invalid" : ""}`}>
                                <ReactQuill
                                    theme="snow"
                                    value={form.message}
                                    onChange={handleReactQuillChange}
                                    modules={modules}
                                    formats={formats}
                                    readOnly = {form.id != '' && form.id != null && !isEdit}
                                />
                            </div>
                            <Form.Control.Feedback type="invalid">
                                {errors.message}
                            </Form.Control.Feedback>
                        </Col>
                    </Row>
                    <Row className="mb-3">
                        <Col md={4}>
                            <div className={`d-flex align-items-center ${(errors.startDate || errors.endDate) ? "is-invalid" : ""}`}>
                                {/*<span className="me-2">Select date range:</span>*/}

                                <div className="date-picker-container-banner position-relative">
                                    <DatePicker
                                        disabled={form.id != '' && form.id != null && !isEdit}
                                        selected={startDate}
                                        onChange={handleDateChange}
                                        selectsRange
                                        startDate={startDate}
                                        endDate={endDate}
                                        className="form-control custom-datepicker-banner"
                                        placeholderText="Select Date Range"
                                        dateFormat="MM/dd/yyyy"
                                        value={formattedDateRange}
                                    />

                                    <img
                                        src={calendarIcon}
                                        alt="Calendar Icon"
                                        className="position-absolute"
                                        style={{
                                            width: "15px",
                                            height: "16.67px",
                                            top: "50%",
                                            left: "190px",
                                            transform: "translateY(-50%)",
                                            opacity: "0.7"
                                        }}
                                    />
                                </div>
                            </div>
                            <Form.Control.Feedback type="invalid">
                                {
                                    (errors.startDate != null && errors.endDate != null) ? (
                                        <span>{BannerConstants.ERROR_DATE_REQUIRED}</span>
                                    ) : errors.startDate ? (
                                        <span>{errors.startDate}</span>
                                    ) : errors.endDate ? (
                                        <span>{errors.endDate}</span>
                                    ) : (
                                        <p>Success!</p>
                                    )
                                }
                            </Form.Control.Feedback>
                        </Col>
                        <Col md={4}>
                            <div className="d-flex align-items-center">
                                <span className="pe-4">
                                    Visibility
                                </span>
                                <ToggleSwitch disabled={form.id != '' && form.id != null && !isEdit} isOn={isOn} handleToggle={handleToggle} onLabel="On" offLabel="Off" />
                            </div>
                        </Col>
                    </Row>
                    <div className="d-flex justify-content-end gap-2 mt-4">
                        <button type="button" className="d-flex align-items-center btn btn-primary rounded-pill" onClick={() => previewBanner()}>Preview</button>
                        {(form.id && !isEdit) && <>
                           {/* <button type="button" className="d-flex align-items-center btn btn-danger rounded-pill" onClick={() => confirmDelete()}>Delete</button> */} 
                            <button type="button" className="d-flex align-items-center btn btn-primary rounded-pill" onClick={() => editBanner()}>Edit</button> </>
                        }
                        {isEdit && 
                         <>
                            <button type="button" className="d-flex align-items-center btn btn-primary rounded-pill" onClick={() => updateBanner()}>Save</button>
                            <button type="button" className="d-flex align-items-center btn btn-secondary rounded-pill" onClick={() => cancelEditBanner()}>Cancel</button>
                         </>
                        }
                        {!form.id && <> <button type="submit" className="d-flex align-items-center btn btn-primary rounded-pill">Publish</button>  </>}
                    </div>
                </Form>
            </div>
            <ConfirmationModal
                show={shouldDisplayDeleteConfirmModal}
                title={BannerConstants.DELETE_CONFIRMATION_TITLE}
                message={BannerConstants.BANNER_DELETED_Confirmation}
                onConfirm={handleDelete}
                onCancel={handleCloseModal}
                onHide={handleCloseModal}
                cancelBtnText="Cancel"
                confirmBtnText="Remove"
                confirmVariant="danger"
            />
        </>
    );
};

export default BannerForm;